import axios, { AxiosInstance, AxiosResponse } from "axios";
import {
    LoginRequest,
    LoginResponse,
    ApiResponse,
    Supplier,
    SupplierStats,
    User,
    PageStatsResponse,
    MediaStatsResponse,
    Media,
    Client,
    DeviceStatsResponse,
    FlightStatsResponse,
    SiteFormRecord,
    SiteFormMailingSetting,
} from "../types";

interface RequestOptions {
    signal?: AbortSignal;
    timeout?: number;
}

class ApiService {
    private api: AxiosInstance;
    private baseURL = window.location.hostname !== "localhost" ? "/api" : "http://localhost:3100/api";
    private mediaSummarySupported = true;
    private readonly statsRequestTimeoutMs = 180000;

    private unwrapApiEnvelope<T = any>(value: any): T {
        if (
            value &&
            typeof value === "object" &&
            !Array.isArray(value) &&
            Object.prototype.hasOwnProperty.call(value, "success") &&
            value.success === false
        ) {
            const message = value.message || "Request failed";
            const error = new Error(message) as Error & { response?: { data: any } };
            error.response = { data: value };
            throw error;
        }

        if (
            value &&
            typeof value === "object" &&
            !Array.isArray(value) &&
            "data" in value &&
            (Object.prototype.hasOwnProperty.call(value, "success") ||
                Object.prototype.hasOwnProperty.call(value, "code") ||
                Object.prototype.hasOwnProperty.call(value, "message"))
        ) {
            return value.data as T;
        }
        return value as T;
    }

    private normalizeAggregatePayload(value: any) {
        const payload = this.unwrapApiEnvelope<any>(value);
        const aggregateSource =
            Array.isArray(payload) &&
            value &&
            typeof value === "object" &&
            !Array.isArray(value) &&
            value.data === payload
                ? value
                : payload;

        return { payload, aggregateSource };
    }

    constructor() {
        this.api = axios.create({
            baseURL: this.baseURL,
            headers: {
                "Content-Type": "application/json",
            },
        });

        // Добавляем токен к каждому запросу
        this.api.interceptors.request.use((config) => {
            const token = localStorage.getItem("token");
            if (token) {
                config.headers.token = token;
            }
            // Добавляем timestamp для предотвращения кеширования
            config.params = {
                ...config.params,
                _t: Date.now(),
            };
            return config;
        });

        // Обрабатываем ошибки авторизации
        this.api.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    window.location.href = "/login";
                }
                return Promise.reject(error);
            },
        );
    }

    private clampDateRange(startDate?: string, endDate?: string, maxDays = 120): { startDate?: string; endDate?: string } {
        if (!startDate || !endDate) return { startDate, endDate };
        const start = new Date(startDate);
        const end = new Date(endDate);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return { startDate, endDate };
        const diffMs = end.getTime() - start.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        if (diffDays <= maxDays) return { startDate, endDate };
        const clampedStart = new Date(end);
        clampedStart.setDate(clampedStart.getDate() - maxDays);
        return {
            startDate: clampedStart.toISOString().split("T")[0],
            endDate,
        };
    }

    // Получение заголовков авторизации
    private getAuthHeaders() {
        const token = localStorage.getItem("token");
        return {
            "Content-Type": "application/json",
            ...(token && { token }),
        };
    }

    // Авторизация
    async login(credentials: LoginRequest): Promise<LoginResponse> {
        // Для debug режима используем хардкод (если логин известный)
        const debugUsers: { [key: string]: { token: string; user: User; password: string } } = {
            "admin": {
                token: "admin-token-123",
                user: {
                    id: 1,
                    login: "admin",
                    email: "admin@system.com",
                    name: "Администратор системы",
                    role: "admin",
                    supplierId: null,
                    clientId: null,
                },
                password: "admin123",
            },
            "okko": {
                token: "okko-token-123",
                user: {
                    id: 2,
                    login: "okko",
                    email: "okko@supplier.com",
                    name: "Пользователь ОККО",
                    role: "supplier",
                    supplierId: 1,
                    clientId: null,
                },
                password: "user123",
            },
            "wink": {
                token: "wink-token-123",
                user: {
                    id: 3,
                    login: "wink",
                    email: "wink@supplier.com",
                    name: "Пользователь Wink",
                    role: "supplier",
                    supplierId: 2,
                    clientId: null,
                },
                password: "user123",
            },
            "premiere": {
                token: "premiere-token-123",
                user: {
                    id: 4,
                    login: "premiere",
                    email: "premiere@supplier.com",
                    name: "Пользователь Premiere",
                    role: "supplier",
                    supplierId: 3,
                    clientId: null,
                },
                password: "user123",
            },
        };

        // Проверяем, является ли это debug пользователем
        if (debugUsers[credentials.login] && debugUsers[credentials.login].password === credentials.password) {
            return {
                token: debugUsers[credentials.login].token,
                user: debugUsers[credentials.login].user,
            };
        }

        // Для реальных пользователей делаем запрос к API
        try {
            const response = await this.api.post("/auth/login", credentials);
            
            if (response.data.success && response.data.data) {
                return {
                    token: response.data.data.token,
                    user: response.data.data.user,
                };
            } else {
                throw new Error(response.data.message || "Ошибка авторизации");
            }
        } catch (error: any) {
            console.error("Login error:", error);
            throw new Error(error.response?.data?.message || "Неверные учетные данные");
        }
    }

    // Получение текущего пользователя
    async getCurrentUser(): Promise<User> {
        // Получаем токен из localStorage
        const token = localStorage.getItem("token");
        if (!token) {
            throw new Error("Пользователь не авторизован");
        }

        // Для debug режима используем хардкод
        if (token === "admin-token-123" || token === "okko-token-123" || token === "wink-token-123" || token === "premiere-token-123") {
            switch (token) {
                case "admin-token-123":
                    return {
                        id: 1,
                        login: "admin",
                        email: "admin@system.com",
                        name: "Администратор системы",
                        role: "admin",
                        supplierId: null,
                        clientId: null,
                    };
                case "okko-token-123":
                    return {
                        id: 2,
                        login: "okko",
                        email: "okko@supplier.com",
                        name: "Пользователь ОККО",
                        role: "supplier",
                        supplierId: 1,
                        clientId: null,
                    };
                case "wink-token-123":
                    return {
                        id: 3,
                        login: "wink",
                        email: "wink@supplier.com",
                        name: "Пользователь Wink",
                        role: "supplier",
                        supplierId: 2,
                        clientId: null,
                        mustChangePassword: false,
                    };
                case "premiere-token-123":
                    return {
                        id: 4,
                        login: "premiere",
                        email: "premiere@supplier.com",
                        name: "Пользователь Premiere",
                        role: "supplier",
                        supplierId: 3,
                        clientId: null,
                        mustChangePassword: false,
                    };
            }
        }

        // Для реального режима делаем запрос к API
        try {
            const response = await this.api.get("/auth/me");
            return response.data.data;
        } catch (error: any) {
            console.error("Error fetching current user:", error);
            throw new Error("Не удалось получить данные пользователя");
        }
    }

    // Поставщики
    async getSuppliers(): Promise<Supplier[]> {
        // Запрашиваем все поставщики с пагинацией (limit максимум 100)
        try {
            // Сначала получаем первую страницу с all=true
            const response: AxiosResponse<ApiResponse<{ data: Supplier[]; total: number; page: number; limit: number; totalPages: number }>> = await this.api.get("/suppliers?page=1&limit=100&all=true");
            // API возвращает {success, data: {data: [...], total, page, limit, totalPages}}
            const responseData = response.data.data;
            const firstPage = Array.isArray(responseData?.data) ? responseData.data : (Array.isArray(responseData) ? responseData : []);
            const total = responseData?.total || 0;
            const totalPages = responseData?.totalPages || 1;
            
            // Если есть еще страницы, загружаем их
            if (totalPages > 1) {
                const allPages = [firstPage];
                for (let page = 2; page <= totalPages; page++) {
                    try {
                        const pageResponse: AxiosResponse<ApiResponse<{ data: Supplier[]; total: number; page: number; limit: number; totalPages: number }>> = await this.api.get(`/suppliers?page=${page}&limit=100&all=true`);
                        const pageResponseData = pageResponse.data.data;
                        const pageData = Array.isArray(pageResponseData?.data) ? pageResponseData.data : (Array.isArray(pageResponseData) ? pageResponseData : []);
                        allPages.push(pageData);
                    } catch (pageError) {
                        console.error(`Error fetching suppliers page ${page}:`, pageError);
                    }
                }
                return allPages.flat();
            }
            
            return firstPage;
        } catch (error) {
            console.error("Error fetching suppliers:", error);
            // Fallback: запрашиваем без параметра all
            try {
                const response: AxiosResponse<ApiResponse<{ data: Supplier[]; total: number; page: number; limit: number; totalPages: number }>> = await this.api.get("/suppliers?page=1&limit=100");
                const responseData = response.data.data;
                return Array.isArray(responseData?.data) ? responseData.data : (Array.isArray(responseData) ? responseData : []);
            } catch (fallbackError) {
                console.error("Error fetching suppliers (fallback):", fallbackError);
                return [];
            }
        }
    }

    async getSupplierById(id: number): Promise<Supplier> {
        const response: AxiosResponse<ApiResponse<Supplier>> = await this.api.get(`/suppliers/${id}`);
        return response.data.data;
    }

    // Получение клиентов для фильтрации
    async getClients(all = false): Promise<any> {
        const url = all ? `${this.baseURL}/clients?all=true` : `${this.baseURL}/clients`;
        const response = await fetch(url, {
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error("Failed to fetch clients");
        }

        const result = await response.json();
        // API возвращает {success: true, data: [...]} - массив напрямую
        // Нормализуем для совместимости с другими эндпоинтами
        return {
            ...result,
            data: {
                data: Array.isArray(result.data) ? result.data : [],
                total: result.total || (Array.isArray(result.data) ? result.data.length : 0),
            }
        };
    }

    // Обновление клиента
    async updateClient(id: number, data: { isEnabled?: boolean; name?: string; code?: string }): Promise<any> {
        const response = await this.api.patch(`/clients/${id}`, data);
        return response.data;
    }

    // Получение статистики по страницам
    async getPagesStats(
        startDate?: string,
        endDate?: string,
        clientId?: number,
        supplierId?: number,
        options?: RequestOptions,
    ): Promise<any> {
        const clamped = this.clampDateRange(startDate, endDate);
        if (clamped.startDate !== startDate || clamped.endDate !== endDate) {
            console.warn("[API] getPagesStats date range clamped", { from: { startDate, endDate }, to: clamped });
        }
        const params: any = {
            startDate: clamped.startDate,
            endDate: clamped.endDate,
            clientId: clientId || undefined,
            supplierId: supplierId || undefined,
        };
        const response = await this.api.get("/pages/stats", {
            params,
            timeout: options?.timeout ?? this.statsRequestTimeoutMs,
            signal: options?.signal,
        });
        return response.data;
    }

    // Получение топ страниц
    async getTopPages(limit = 10, startDate?: string, endDate?: string, clientId?: number): Promise<any> {
        const params = new URLSearchParams();
        params.append("limit", limit.toString());
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        if (clientId) params.append("clientId", clientId.toString());

        const response = await fetch(`${this.baseURL}/pages/top-pages?${params}`, {
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error("Failed to fetch top pages");
        }

        return response.json();
    }

    // Получение статистики по медиа
    async getMediaStats(
        startDate?: string,
        endDate?: string,
        clientId?: number,
        supplierId?: number,
        options?: RequestOptions,
    ): Promise<any> {
        const clamped = this.clampDateRange(startDate, endDate);
        if (clamped.startDate !== startDate || clamped.endDate !== endDate) {
            console.warn("[API] getMediaStats date range clamped", { from: { startDate, endDate }, to: clamped });
        }
        const params: any = {
            startDate: clamped.startDate,
            endDate: clamped.endDate,
            clientId: clientId || undefined,
            supplierId: supplierId || undefined,
        };
        const response = await this.api.get("/media-stats/stats", {
            params,
            timeout: options?.timeout ?? this.statsRequestTimeoutMs,
            signal: options?.signal,
        });
        return response.data;
    }

    async getMediaSummary(
        startDate?: string,
        endDate?: string,
        clientId?: number,
        supplierId?: number,
        options?: RequestOptions,
    ): Promise<any> {
        if (!this.mediaSummarySupported) {
            return this.getMediaStats(startDate, endDate, clientId, supplierId, options);
        }

        const clamped = this.clampDateRange(startDate, endDate);
        const params: any = {
            startDate: clamped.startDate,
            endDate: clamped.endDate,
            clientId: clientId || undefined,
            supplierId: supplierId || undefined,
        };

        try {
            const response = await this.api.get("/media-stats/summary", {
                params,
                timeout: options?.timeout ?? this.statsRequestTimeoutMs,
                signal: options?.signal,
            });
            return response.data;
        } catch (error: any) {
            if (error?.response?.status === 404) {
                this.mediaSummarySupported = false;
                return this.getMediaStats(startDate, endDate, clientId, supplierId, options);
            }
            throw error;
        }
    }

    // Получение топ медиа
    async getTopMedia(limit = 10, startDate?: string, endDate?: string, clientId?: number): Promise<any> {
        const params = new URLSearchParams();
        params.append("limit", limit.toString());
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        if (clientId) params.append("clientId", clientId.toString());

        const response = await fetch(`${this.baseURL}/media-stats/top-media?${params}`, {
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error("Failed to fetch top media");
        }

        return response.json();
    }

    // Экспорт статистики страниц в CSV
    async exportPagesStats(startDate?: string, endDate?: string, clientId?: number): Promise<Blob> {
        const params = new URLSearchParams();
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        if (clientId) params.append("clientId", clientId.toString());
        params.append("export", "true"); // Добавляем параметр экспорта

        const response = await fetch(`${this.baseURL}/pages/stats?${params}`, {
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error("Failed to export pages stats");
        }

        return response.blob();
    }

    // Экспорт статистики медиа в CSV
    async exportMediaStats(startDate?: string, endDate?: string, clientId?: number): Promise<Blob> {
        const params = new URLSearchParams();
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        if (clientId) params.append("clientId", clientId.toString());

        const response = await fetch(`${this.baseURL}/media-stats/export?${params}`, {
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error("Failed to export media stats");
        }

        return response.blob();
    }

    // Статистика (для обратной совместимости)
    async getSupplierStats(supplierId: number, startDate: Date, endDate: Date, clientId?: number | null): Promise<SupplierStats> {
        // Используем реальные данные из API
        const startDateStr = startDate.toISOString().split("T")[0];
        const endDateStr = endDate.toISOString().split("T")[0];

        try {
            const [mediaStats, pagesStats] = await Promise.all([
                this.getMediaStats(startDateStr, endDateStr, clientId || undefined, supplierId),
                this.getPagesStats(startDateStr, endDateStr, clientId || undefined, supplierId),
            ]);

            // API возвращает {success, data: {data: [], totalViewCount, ...}}
            // Извлекаем данные из вложенной структуры
            const mediaDataArray = Array.isArray(mediaStats.data?.data) 
                ? mediaStats.data.data 
                : Array.isArray(mediaStats.data) 
                ? mediaStats.data 
                : [];
            const totalViewCount = mediaStats.data?.totalViewCount || 0;

            const stats = mediaDataArray.map((item: any) => ({
                    mediaId: item.mediaId || Math.floor(Math.random() * 1000),
                    mediaName: item.mediaTitle || "Unknown Media",
                    viewCount: item.viewCount?.toString() || "0",
            }));

            return {
                stats,
                summary: {
                    totalViews:
                        totalViewCount ||
                        mediaDataArray.reduce((sum: number, item: any) => sum + (item.viewCount || 0), 0) ||
                        0,
                    period: {
                        startDate: startDate.toISOString(),
                        endDate: endDate.toISOString(),
                    },
                    supplierId,
                },
            };
        } catch (error) {
            console.error("Error fetching real stats, using fallback:", error);

            // Fallback к тестовым данным
            const testStats: SupplierStats = {
                stats: [
                    { mediaId: 1, mediaName: "Фильм 1", viewCount: "1250" },
                    { mediaId: 2, mediaName: "Фильм 2", viewCount: "980" },
                    { mediaId: 3, mediaName: "Сериал 1", viewCount: "750" },
                ],
                summary: {
                    totalViews: 2980,
                    period: {
                        startDate: startDate.toISOString(),
                        endDate: endDate.toISOString(),
                    },
                    supplierId,
                },
            };

            return testStats;
        }
    }

    // Тестовые данные
    async getTestData(): Promise<any> {
        const response: AxiosResponse<ApiResponse<any>> = await this.api.get("/stat/test");
        return response.data.data;
    }

    // Статистика по устройствам
    async getDeviceStats(
        clientId: number | null,
        startDate: Date,
        endDate: Date,
        supplierId?: number,
        options?: RequestOptions,
    ): Promise<DeviceStatsResponse> {
        const clamped = this.clampDateRange(
            startDate.toISOString().split("T")[0],
            endDate.toISOString().split("T")[0],
        );
        const params = {
            clientId: clientId || undefined,
            startDate: clamped.startDate, // Только дата без времени
            endDate: clamped.endDate,
            supplierId: supplierId || undefined,
        };
        const response = await this.api.get("/devices/stats", {
            params,
            timeout: options?.timeout ?? this.statsRequestTimeoutMs,
            signal: options?.signal,
        });
        const { payload, aggregateSource } = this.normalizeAggregatePayload(response.data);
        const rows = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
        return {
            data: rows,
            total: aggregateSource?.total ?? rows.length,
            totalViews: aggregateSource?.totalViews,
            totalPageViews: aggregateSource?.totalPageViews,
            totalMediaViews: aggregateSource?.totalMediaViews,
            totalUniqueVisitors: aggregateSource?.totalUniqueVisitors,
        };
    }

    // Статистика по рейсам
    async getFlightStats(
        clientId: number | null,
        startDate: Date,
        endDate: Date,
        supplierId?: number,
        options?: RequestOptions,
    ): Promise<FlightStatsResponse> {
        const clamped = this.clampDateRange(
            startDate.toISOString().split("T")[0],
            endDate.toISOString().split("T")[0],
        );
        const params = {
            clientId: clientId || undefined,
            startDate: clamped.startDate, // Только дата без времени
            endDate: clamped.endDate,
            supplierId: supplierId || undefined,
        };
        const response = await this.api.get("/devices/flights", {
            params,
            timeout: options?.timeout ?? this.statsRequestTimeoutMs,
            signal: options?.signal,
        });
        const { payload, aggregateSource } = this.normalizeAggregatePayload(response.data);
        const rows = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
        return {
            data: rows,
            total: aggregateSource?.total ?? rows.length,
            totalViews: aggregateSource?.totalViews,
            totalPageViews: aggregateSource?.totalPageViews,
            totalMediaViews: aggregateSource?.totalMediaViews,
            totalUniqueVisitors: aggregateSource?.totalUniqueVisitors,
        };
    }

    /** Ответы форм с борта (site-forms) */
    async getSiteForms(params: {
        formType?: string;
        startDate?: string;
        endDate?: string;
    }): Promise<{ items: SiteFormRecord[]; total: number }> {
        const response = await this.api.get("/site-forms", { params });
        const payload = this.unwrapApiEnvelope<any>(response.data);
        const items: SiteFormRecord[] = Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];
        return { items, total: typeof payload?.total === "number" ? payload.total : items.length };
    }

    async getAuroraSiteFormMailingSettings(): Promise<SiteFormMailingSetting[]> {
        const response = await this.api.get("/site-forms/mailing-settings/aurora");
        const payload = this.unwrapApiEnvelope<any>(response.data);
        return Array.isArray(payload) ? payload : [];
    }

    async updateAuroraSiteFormMailingSetting(
        formType: "feedback" | "landing" | "logbook",
        payload: { recipients: string[]; fridayTime: string; isEnabled: boolean; scheduleType: "friday" | "daily" },
    ): Promise<SiteFormMailingSetting> {
        const response = await this.api.put(`/site-forms/mailing-settings/aurora/${formType}`, payload);
        return this.unwrapApiEnvelope<SiteFormMailingSetting>(response.data);
    }

    async testSendAuroraSiteFormMailing(formType: "feedback" | "landing" | "logbook"): Promise<void> {
        await this.api.post(`/site-forms/mailing-settings/aurora/${formType}/test-send`);
    }

    // Управление видимостью медиа в статистике (только для админа)
    async getMediaVisibilityList(): Promise<Media[]> {
        const response = await this.api.get("/media-stats/visibility");
        // API возвращает {success, data: {success, data: [...]}}
        // Извлекаем массив из вложенной структуры
        const data = response.data?.data;
        if (Array.isArray(data)) {
            return data;
        }
        if (data && Array.isArray(data.data)) {
            return data.data;
        }
        return [];
    }

    async getMediaRenameList(): Promise<Media[]> {
        return this.getMediaVisibilityList();
    }

    async updateMediaVisibility(mediaId: number, isVisibleInStats: boolean): Promise<void> {
        await this.api.put(`/media-stats/visibility/${mediaId}`, { isVisibleInStats });
    }

    async updateMediaName(mediaId: number, name: string, parentId?: number | null): Promise<Media> {
        const body: { name: string; parentId?: number | null } = { name };
        if (parentId !== undefined) {
            body.parentId = parentId;
        }
        const response = await this.api.put(`/media-stats/name/${mediaId}`, body);
        const payload = this.unwrapApiEnvelope<any>(response.data);
        return payload?.data || payload;
    }

    // Очистка кэша (только для админа)
    async clearCache(): Promise<void> {
        await this.api.post("/cache/clear");
    }

    // Управление пользователями (только для админа)
    async getUsers(pagination?: { page: number; limit: number }, all?: boolean): Promise<ApiResponse<any>> {
        const params: any = {};
        if (all) {
            // Если нужны все пользователи, используем all=true без пагинации
            params.all = "true";
        } else if (pagination) {
            // Иначе используем пагинацию (limit не должен превышать 100)
            params.page = pagination.page;
            params.limit = Math.min(pagination.limit, 100); // Ограничиваем максимум 100
        }
        const response = await this.api.get("/users", { params });
        return response.data;
    }

    async getUserById(id: number): Promise<User> {
        const response = await this.api.get(`/users/${id}`);
        return response.data.data || response.data;
    }

    async createUser(userData: {
        login: string;
        password: string;
        name?: string | null;
        email?: string | null;
        supplierId?: number | null;
        clientId?: number | null;
        isLoginEnabled?: boolean;
        isEnabled?: boolean;
        mustChangePassword?: boolean;
        comment?: string | null;
    }): Promise<void> {
        await this.api.post("/users", userData);
    }

    async updateUser(id: number, userData: {
        login?: string;
        password?: string;
        name?: string | null;
        email?: string | null;
        supplierId?: number | null;
        clientId?: number | null;
        isLoginEnabled?: boolean;
        isEnabled?: boolean;
        mustChangePassword?: boolean;
        comment?: string | null;
    }): Promise<void> {
        await this.api.put(`/users/${id}`, userData);
    }

    async deleteUser(id: number): Promise<void> {
        await this.api.delete(`/users/${id}`);
    }

    async resetUserPassword(id: number, newPassword: string): Promise<void> {
        await this.api.post(`/users/${id}/reset-password`, { password: newPassword });
    }

    // Смена пароля текущим пользователем
    async changePassword(currentPassword: string | undefined, newPassword: string): Promise<void> {
        const body: any = { newPassword };
        if (currentPassword) {
            body.currentPassword = currentPassword;
        }
        await this.api.put("/auth/change-password", body);
    }
}

export const apiService = new ApiService();
